import { PriceDisplay } from "@modules/shared/Product/PriceDisplay";
import { Button } from "@ui/components/ui/button";
import { Link } from "@tanstack/react-router";
import { useLocale } from "@modules/shared/hooks/useLocale";
import {
  CartLineQuantity,
  Image,
  Money,
  useCart,
  useCartLine,
} from "@shopify/hydrogen-react";
import { useTranslation } from "react-i18next";
import { Trash2 } from "lucide-react";
import type { MoneyV2 } from "@shopify/hydrogen-react/storefront-api-types";

export const ProductAddedToCartListItem: React.FC = () => {
  const { t } = useTranslation("SingleProductPage");
  const locale = useLocale();
  const { merchandise, attributes, quantity, discountAllocations, cost, id } =
    useCartLine();
  const { linesRemove } = useCart();

  const productQuantity = quantity;
  const productPrice: {
    priceRange: {
      minVariantPrice: MoneyV2;
    };
    compareAtPriceRange: {
      maxVariantPrice: MoneyV2;
    };
  } = {
    priceRange: {
      minVariantPrice: {
        amount: cost?.subtotalAmount?.amount ?? "0",
        currencyCode: "EUR",
      },
    },
    compareAtPriceRange: {
      maxVariantPrice: {
        amount:
          (
            Number(merchandise?.compareAtPrice?.amount) * productQuantity
          ).toString(),
        currencyCode: "EUR",
      },
    },
  };

  const productDiscountedPrice = discountAllocations?.reduce(
    (acc, discount) => acc + Number(discount?.discountedAmount?.amount),
    0
  );

  return (
    <div className="flex w-full flex-col gap-3 bg-white">
      <div className="flex gap-3">
        {merchandise?.image && (
          <Link
            to="/$locale/product/$handle"
            params={{
              locale,
              handle: merchandise.product?.handle ?? "",
            }}
            className="h-[60px] w-[46px]"
          >
            <Image
              data={merchandise.image}
              alt={merchandise.image.altText ?? merchandise.title}
              width={46}
              height={60}
              className="h-[60px] w-[46px] rounded-lg bg-transparent"
              sizes="46px"
            />
          </Link>
        )}
        <div className="flex w-full flex-col justify-between gap-3">
          <Link
            to="/$locale/product/$handle"
            params={{ locale, handle: merchandise?.product?.handle ?? "" }}
            className="text-headline-small hover:text-primary"
          >
            {merchandise?.product?.title}
          </Link>
          <div className="font-primary text-tny">
            {attributes?.map(
              attribute =>
                attribute &&
                attribute.key !== "Title" && (
                  <div key={attribute.key}>
                    <span className="text-text-4">
                      {t(attribute.key as any)}:{" "}
                    </span>
                    <span className="text-text-1">{attribute.value}</span>
                  </div>
                )
            )}
            <div>
              <span className="text-text-1">{t("quantity")}: </span>
              <CartLineQuantity className="text-text-1" />
            </div>
            {productDiscountedPrice && productDiscountedPrice > 0 ? (
              <div className="flex gap-2">
                <div className="text-text-4 line-through">
                  <PriceDisplay
                    productPriceClassName="text-tny"
                    product={productPrice}
                  />
                </div>
                {cost?.totalAmount && (
                  <Money data={cost.totalAmount} className="text-tny" />
                )}
              </div>
            ) : (
              <PriceDisplay
                productPriceClassName="text-tny"
                product={productPrice}
              />
            )}
          </div>
        </div>
        <Button
          className="min-h-[16px] min-w-[16px] !p-0"
          light="ghost"
          onClick={e => {
            e.stopPropagation();
            if (!id) return;
            linesRemove([id]);
          }}
        >
          <Trash2 size={16} />
        </Button>
      </div>
    </div>
  );
};
